import { DragDropModule } from '@angular/cdk/drag-drop';
import { HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, Injector, NgModule } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { DropDownListModule, DropDownsModule, MultiSelectModule } from '@progress/kendo-angular-dropdowns';
import { EditorModule } from '@progress/kendo-angular-editor';
import { ExcelModule, ExcelService, GridModule } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { L10N_PREFIX, LocalizationService } from '@progress/kendo-angular-l10n';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { MenusModule } from '@progress/kendo-angular-menu';
import { TreeModule } from 'angular-tree-component';
import { ColorSketchModule } from 'ngx-color/sketch';
import { PDAuthModule } from 'pdauth';

import { Api } from '../api/api';
import { DataDictionaryApi } from '../api/data-dictionary-api';
import { FilterApi } from '../api/filter-api';
import { Http2Factory } from '../api/http2-factory';
import { LayerApi } from '../api/layer-api';
import { MapApi } from '../api/map-api';
import { SystemMessagesApi } from '../api/system-message-api';
import { UserApi } from '../api/user-api';
import { LayerKeyPipe } from '../components/layerComponents/legend-data/layer-key.pipe';
import { NearbyFilterByPipe } from '../components/nearby/nearby.pipe';
import { AttributeDatePipe } from '../pipes/data-attributeDate.pipe';
import { DataFilterPipe } from '../pipes/data-filter.pipe';
import { DataPaginatePipe } from '../pipes/data-paginate.pipe';
import { ComponentService } from '../services/component.service';
import { FilterService } from '../services/filter.service';
import { AuthenticationServiceProvider } from '../services/interfaces/IAuthenticationService';
import { LoggingServiceProvider } from '../services/interfaces/ILoggingService';
import { UserSettingsServiceProvider } from '../services/interfaces/IUserSettingsService';
import { LayerService } from '../services/layer.service';
import { LayerCatalogService } from '../services/layerCatalog.service';
import { LegendService } from '../services/legend.service';
import { MapService } from '../services/map.service';
import { NotifyService } from '../services/notify.service';
import { SplitThemeService } from '../services/split-theme.service';
import { SystemMessagesService } from '../services/system-messages.service';
import { ThemeService } from '../services/theme.service';
import { ToSqlStringService } from '../services/to-sql-string.service';
import { UserService } from '../services/user.service';

import './tag-maps';

/* tslint:disable:ordered-imports */
import { AddressComponent } from './aoiComponents/address/address.component';
import { AssetSearchComponent } from './aoiComponents/assetSearch/asset-search.component';
import { IntersectionComponent } from './aoiComponents/intersection/intersection.component';
import { InterstateComponent } from './aoiComponents/interstate/interstate.component';
import { LatLongComponent } from './aoiComponents/latLong/lat-long.component';
import { RegionComponent } from './aoiComponents/region/region.component';
import { RouteComponent } from './aoiComponents/route/route.component';
import { StateComponent } from './aoiComponents/state/state.component';
import { AuthComponent } from './authenticationComponents/auth.component';
import { BookmarkComponent } from './bookmark/bookmark.component';
import { BufferComponent } from './buffer/buffer.component';
import { DocSourceItemComponent } from './docSourceComponents/doc-source-item/doc-source-item.component';
import { DocSourcePhotoComponent } from './docSourceComponents/doc-source-photo/doc-source-photo.component';
import { DocSourceVideoComponent } from './docSourceComponents/doc-source-video/doc-source-video.component';
import { DocSourceComponent } from './docSourceComponents/doc-source/doc-source.component';
import { DrawIntersectionComponent } from './drawComponents/draw-intersection/draw-intersection.component';
import { DrawLineComponent } from './drawComponents/draw-line/draw-line.component';
import { DrawPointComponent } from './drawComponents/draw-point/draw-point.component';
import { DrawPolygonComponent } from './drawComponents/draw-polygon/draw-polygon.component';
import { DrawTextComponent } from './drawComponents/draw-text/draw-text.component';
import { ExtentComponent } from './extent/extent.component';
import { BasicFilterComponent } from './filterComponents/basic-filter/basic-filter.component';
import { ChildRecordsFilterComponent } from './filterComponents/child-records-filter/child-records-filter.component';
import { GeographicFilterComponent } from './filterComponents/geographic-filter/geographic-filter.component';
import { GlobalFilterComponent } from './filterComponents/global-filter/global-filter.component';
import { JoinFilterComponent } from './filterComponents/join-filter/join-filter.component';
import { LayerFilterContainerComponent } from './filterComponents/layer-filter-container/layer-filter-container.component';
import { LayerFilterSelectorComponent } from './filterComponents/layer-filter-selector/layer-filter-selector.component';
import { IdentifyComponent } from './identify/identify.component';
import { AddExternalLayerComponent } from './layerComponents/add-external-layer/add-external-layer.component';
import { DataTableAdditionalInformationComponent } from './layerComponents/data-table/additional-information.component';
import { DataTableComponent } from './layerComponents/data-table/data-table.component';
import { ExportLayerComponent } from './layerComponents/export-layer/export-layer.component';
import { ExternalLayerCatalogComponent } from './layerComponents/external-layer-catalog/external-layer-catalog.component';
import { LassoToolComponent } from './layerComponents/lasso-tool/lasso-tool.component';
import { LayerCatalogItemComponent } from './layerComponents/layer-catalog-item/layer-catalog-item.component';
import { LayerCatalogComponent } from './layerComponents/layer-catalog/layer-catalog.component';
import { LayerDetailComponent } from './layerComponents/layer-detail/layer-detail.component';
import { LayerLabelsComponent } from './layerComponents/layer-labels/layer-labels.component';
import { LayerSpatialQueryComponent } from './layerComponents/layer-spatial-query/layer-spatial-query.component';
import { LayerTooltipComponent } from './layerComponents/layer-tooltip/layer-tooltip.component';
import { LegendDataComponent } from './layerComponents/legend-data/legend-data.component';
import { LegendSymbolComponent } from './layerComponents/legend-symbol/legend-symbol.component';
import { LegendComponent } from './layerComponents/legend/legend.component';
import { MinMaxScaleComponent } from './layerComponents/min-max-scale/min-max-scale.component';
import { RecordEditorComponent } from './layerComponents/record-editor/record-editor.component';
import { SaveLayerComponent } from './layerComponents/save-layer/save-layer.component';
import { ShareLayerComponent } from './layerComponents/share-layer/share-layer.component';
import { SplitThemeEditorComponent } from './layerComponents/split-theme-editor/split-theme-editor.component';
import { HeatmapEditorComponent } from './layerComponents/symbol-editor/heatmap-editor/heatmap-editor.component';
import { LayerOpacityEditorComponent } from './layerComponents/symbol-editor/layer-opacity-editor/layer-opacity-editor.component';
import { LineSymbolEditorComponent } from './layerComponents/symbol-editor/line-symbol-editor/line-symbol-editor.component';
import { PointSymbolEditorComponent } from './layerComponents/symbol-editor/point-symbol-editor/point-symbol-editor.component';
import { PolygonSymbolEditorComponent } from './layerComponents/symbol-editor/polygon-symbol-editor/polygon-symbol-editor.component';
import { SymbolEditorContainerComponent } from './layerComponents/symbol-editor/symbol-editor-container/symbol-editor-container.component';
import { TextSymbolEditorComponent } from './layerComponents/symbol-editor/text-symbol-editor/text-symbol-editor.component';
import { ThemeEditorComponent } from './layerComponents/theme-editor/theme-editor.component';
import { UnshareLayerComponent } from './layerComponents/unshare-layer/unshare-layer.component';
import { BasemapSwitcherComponent } from './mapComponents/basemap-switcher/basemap-switcher.component';
import { ExpanderComponent } from './mapComponents/expander/expander.component';
import { ExportMapComponent } from './mapComponents/export-map/export-map.component';
import { ImportExternalMapComponent } from './mapComponents/import-external-map/import-external-map.component';
import { ImportShapefileComponent } from './mapComponents/import-shapefile/import-shapefile.component';
import { MapCatalogItemComponent } from './mapComponents/map-catalog-item/map-catalog-item.component';
import { MapCatalogComponent } from './mapComponents/map-catalog/map-catalog.component';
import { MapInfoContainerComponent } from './mapComponents/map-info-container/map-info-container.component';
import { MapPickerComponent } from './mapComponents/map-picker/map-picker.component';
import { MapComponent } from './mapComponents/map/map.component';
import { MergeMapActionComponent } from './mapComponents/merge-map-action/merge-map-action.component';
import { MergeMapComponent } from './mapComponents/merge-map/merge-map.component';
import { OverviewMapComponent } from './mapComponents/overview-map/overview-map.component';
import { SaveMapComponent } from './mapComponents/save-map/save-map.component';
import { ShareMapComponent } from './mapComponents/share-map/share-map.component';
import { SketchToolComponent } from './mapComponents/sketch-tool/sketch-tool.component';
import { UnShareMapComponent } from './mapComponents/unshare-map/unshare-map.component';
import { NearbyComponent } from './nearby/nearby.component';
import { PetlAboutComponent } from './petl/about/about.component';
import { PetlColumnMappingComponent } from './petl/column-mapping/column-mapping.component';
import { PetlManageProjectsComponent } from './petl/manage-projects/manage-projects.component';
import { PetlComponent } from './petl/petl.component';
import { PetlProcessorComponent } from './petl/processor/processor.component';
import { PetlPromotedComponent } from './petl/promoted/promoted.component';
import { PetlStartComponent } from './petl/start/start.component';
import { PetlValidateComponent } from './petl/validate/validate.component';
import { PrintLegendComponent } from './print/print-legend/print-legend.component';
import { PrintComponent } from './print/print.component';
import { RoutingComponent } from './routing/routing.component';
import { ScopeComponent } from './scope/scope.component';
import { LatLongSelectComponent } from './street-view/lat-long-select/lat-long-select.component';
import { RmsSelectComponent } from './street-view/rms-select/rms-select.component';
import { StreetViewComponent } from './street-view/street-view.component';
import { CalloutComponent } from './toolComponents/callout/callout.component';
import { IrowComponent } from './toolComponents/irow/irow.component';
import { IsleComponent } from './toolComponents/isle/isle.component';
import { AreaComponent } from './toolComponents/measurementComponents/area/area.component';
import { CoordinatesComponent } from './toolComponents/measurementComponents/coordinates/coordinates.component';
import { DistanceComponent } from './toolComponents/measurementComponents/distance/distance.component';
import { UserSettingsComponent } from './userSettings/user-settings.component';
import { ApplicationMessagesComponent } from './utilityComponents/applicationMessages/application-messages.component';
import { ColorPickerComponent } from './utilityComponents/colorPicker/colorPicker.component';
import { GalleryComponent } from './utilityComponents/gallery/gallery.component';
import { PennbotsConnectorComponent } from './utilityComponents/pennbots-connector/pennbots-connector.component';
import { SearchComponent } from './utilityComponents/search/search.component';
import { SimpleToggleComponent } from './utilityComponents/simple-toggle/simple-toggle.component';
import { SystemMessagesComponent } from './utilityComponents/systemMessages/system-messages.component';
import { TabsComponent } from './utilityComponents/tabs/tabs.component';
import { TreeExplorerComponent } from './utilityComponents/tree-explorer/tree-explorer.component';
import { WindowComponent } from './utilityComponents/window/window.component';
import { VideoLogControllerComponent } from './videoLog/video-log-controller/video-log-controller.component';
import { VideoLogInfoComponent } from './videoLog/video-log-info/video-log-info.component';
import { VideoLogViewerComponent } from './videoLog/video-log-viewer/video-log-viewer.component';
/* tslint:enable:ordered-imports */

@NgModule({
    declarations: [
        DataFilterPipe,
        AttributeDatePipe,
        DataPaginatePipe,
        NearbyFilterByPipe,
        LayerKeyPipe,
        AddExternalLayerComponent,
        AddressComponent,
        ApplicationMessagesComponent,
        AreaComponent,
        AssetSearchComponent,
        AuthComponent,
        BasemapSwitcherComponent,
        BasicFilterComponent,
        BookmarkComponent,
        BufferComponent,
        CalloutComponent,
        ChildRecordsFilterComponent,
        ColorPickerComponent,
        CoordinatesComponent,
        DataTableAdditionalInformationComponent,
        DataTableComponent,
        DistanceComponent,
        DocSourceComponent,
        DocSourceItemComponent,
        DocSourcePhotoComponent,
        DocSourceVideoComponent,
        DrawIntersectionComponent,
        DrawLineComponent,
        DrawPointComponent,
        DrawPolygonComponent,
        DrawTextComponent,
        ExpanderComponent,
        ExportLayerComponent,
        ExportMapComponent,
        ExtentComponent,
        ExternalLayerCatalogComponent,
        GalleryComponent,
        GeographicFilterComponent,
        GlobalFilterComponent,
        HeatmapEditorComponent,
        IdentifyComponent,
        ImportExternalMapComponent,
        ImportShapefileComponent,
        IntersectionComponent,
        InterstateComponent,
        IrowComponent,
        IsleComponent,
        JoinFilterComponent,
        LassoToolComponent,
        LatLongComponent,
        LatLongSelectComponent,
        LayerCatalogComponent,
        LayerCatalogItemComponent,
        LayerDetailComponent,
        LayerFilterContainerComponent,
        LayerFilterSelectorComponent,
        LayerLabelsComponent,
        LayerOpacityEditorComponent,
        LayerSpatialQueryComponent,
        LayerTooltipComponent,
        LegendComponent,
        LegendDataComponent,
        LegendSymbolComponent,
        LineSymbolEditorComponent,
        MapCatalogComponent,
        MapCatalogItemComponent,
        MapComponent,
        MapInfoContainerComponent,
        MapPickerComponent,
        MergeMapActionComponent,
        MergeMapComponent,
        MinMaxScaleComponent,
        NearbyComponent,
        OverviewMapComponent,
        PennbotsConnectorComponent,
        PetlAboutComponent,
        PetlColumnMappingComponent,
        PetlComponent,
        PetlManageProjectsComponent,
        PetlProcessorComponent,
        PetlPromotedComponent,
        PetlStartComponent,
        PetlValidateComponent,
        PointSymbolEditorComponent,
        PolygonSymbolEditorComponent,
        PrintComponent,
        PrintLegendComponent,
        RecordEditorComponent,
        RegionComponent,
        RmsSelectComponent,
        RouteComponent,
        RoutingComponent,
        SaveLayerComponent,
        SaveMapComponent,
        ScopeComponent,
        SearchComponent,
        ShareLayerComponent,
        ShareMapComponent,
        SimpleToggleComponent,
        SketchToolComponent,
        SplitThemeEditorComponent,
        StateComponent,
        StreetViewComponent,
        SymbolEditorContainerComponent,
        SystemMessagesComponent,
        TabsComponent,
        TextSymbolEditorComponent,
        ThemeEditorComponent,
        TreeExplorerComponent,
        UnShareMapComponent,
        UnshareLayerComponent,
        UserSettingsComponent,
        VideoLogControllerComponent,
        VideoLogInfoComponent,
        VideoLogViewerComponent,
        WindowComponent
    ],
    entryComponents: [
        AddExternalLayerComponent,
        AddressComponent,
        ApplicationMessagesComponent,
        AreaComponent,
        AssetSearchComponent,
        AuthComponent,
        BasemapSwitcherComponent,
        BasicFilterComponent,
        BookmarkComponent,
        BufferComponent,
        CalloutComponent,
        ChildRecordsFilterComponent,
        ColorPickerComponent,
        CoordinatesComponent,
        DataTableAdditionalInformationComponent,
        DataTableComponent,
        DistanceComponent,
        DocSourceComponent,
        DocSourceItemComponent,
        DocSourcePhotoComponent,
        DocSourceVideoComponent,
        DrawIntersectionComponent,
        DrawLineComponent,
        DrawPointComponent,
        DrawPolygonComponent,
        DrawTextComponent,
        ExpanderComponent,
        ExportLayerComponent,
        ExportMapComponent,
        ExtentComponent,
        ExternalLayerCatalogComponent,
        GalleryComponent,
        GeographicFilterComponent,
        GlobalFilterComponent,
        HeatmapEditorComponent,
        IdentifyComponent,
        ImportExternalMapComponent,
        ImportShapefileComponent,
        IntersectionComponent,
        InterstateComponent,
        IrowComponent,
        IsleComponent,
        JoinFilterComponent,
        LassoToolComponent,
        LatLongComponent,
        LatLongSelectComponent,
        LayerCatalogComponent,
        LayerCatalogItemComponent,
        LayerDetailComponent,
        LayerFilterContainerComponent,
        LayerFilterSelectorComponent,
        LayerLabelsComponent,
        LayerOpacityEditorComponent,
        LayerSpatialQueryComponent,
        LayerTooltipComponent,
        LegendComponent,
        LegendDataComponent,
        LegendSymbolComponent,
        LineSymbolEditorComponent,
        MapCatalogComponent,
        MapCatalogItemComponent,
        MapComponent,
        MapInfoContainerComponent,
        MapPickerComponent,
        MergeMapActionComponent,
        MergeMapComponent,
        MinMaxScaleComponent,
        NearbyComponent,
        OverviewMapComponent,
        PennbotsConnectorComponent,
        PetlAboutComponent,
        PetlColumnMappingComponent,
        PetlComponent,
        PetlManageProjectsComponent,
        PetlProcessorComponent,
        PetlPromotedComponent,
        PetlStartComponent,
        PetlValidateComponent,
        PointSymbolEditorComponent,
        PolygonSymbolEditorComponent,
        PrintComponent,
        PrintLegendComponent,
        RecordEditorComponent,
        RegionComponent,
        RmsSelectComponent,
        RouteComponent,
        RoutingComponent,
        SaveLayerComponent,
        SaveMapComponent,
        ScopeComponent,
        SearchComponent,
        ShareLayerComponent,
        ShareMapComponent,
        SimpleToggleComponent,
        SketchToolComponent,
        SplitThemeEditorComponent,
        StateComponent,
        StreetViewComponent,
        SymbolEditorContainerComponent,
        SystemMessagesComponent,
        TabsComponent,
        TextSymbolEditorComponent,
        ThemeEditorComponent,
        TreeExplorerComponent,
        UnShareMapComponent,
        UnshareLayerComponent,
        UserSettingsComponent,
        VideoLogControllerComponent,
        VideoLogInfoComponent,
        VideoLogViewerComponent,
        WindowComponent
    ],
    imports: [
        FormsModule,
        BrowserModule,
        HttpClientModule,
        DialogsModule,
        DropDownListModule,
        DropDownsModule,
        EditorModule,
        ExcelModule,
        GridModule,
        BrowserAnimationsModule,
        ButtonsModule,
        LayoutModule,
        ReactiveFormsModule,
        TreeModule.forRoot(),
        PDAuthModule,
        MultiSelectModule,
        ColorSketchModule,
        MenusModule,
        InputsModule,
        DragDropModule
    ],
    providers: [
        Http2Factory,
        ExcelService,
        LocalizationService,
        { provide: L10N_PREFIX, useValue: '' },
        Api,
        DataDictionaryApi,
        FilterApi,
        LayerApi,
        MapApi,
        SystemMessagesApi,
        UserApi,
        LayerCatalogService,
        FilterService,
        LayerService,
        LegendService,
        ComponentService,
        MapService,
        SplitThemeService,
        ThemeService,
        ToSqlStringService,
        ToSqlStringService,
        AuthenticationServiceProvider,
        LoggingServiceProvider,
        UserSettingsServiceProvider,
        NotifyService,
        SystemMessagesService,
        UserService,
        { provide: 'Window',  useValue: window }
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ]
})
export class MapcoreModule {
    constructor(private readonly injector: Injector) { }

    ngDoBootstrap() {
        customElements.define('mc-basic-filter', createCustomElement(BasicFilterComponent, { injector: this.injector }));
        customElements.define('mc-child-records-filter', createCustomElement(ChildRecordsFilterComponent, { injector: this.injector }));
        customElements.define('mc-geographic-filter', createCustomElement(GeographicFilterComponent, { injector: this.injector }));
        customElements.define('mc-join-filter', createCustomElement(JoinFilterComponent, { injector: this.injector }));
        customElements.define('mc-extent', createCustomElement(ExtentComponent, { injector: this.injector }));
        customElements.define('mc-color-picker', createCustomElement(ColorPickerComponent, { injector: this.injector }));
        customElements.define('mc-data-table', createCustomElement(DataTableComponent, { injector: this.injector }));
        customElements.define('mc-sketch-tool', createCustomElement(SketchToolComponent, { injector: this.injector }));
        customElements.define('mc-lasso-tool', createCustomElement(LassoToolComponent, { injector: this.injector }));
        customElements.define('mc-additional-information', createCustomElement(DataTableAdditionalInformationComponent, { injector: this.injector }));
        customElements.define('mc-layer-catalog', createCustomElement(LayerCatalogComponent, { injector: this.injector }));
        customElements.define('mc-layer-catalog-item', createCustomElement(LayerCatalogItemComponent, { injector: this.injector }));
        customElements.define('mc-layer-detail', createCustomElement(LayerDetailComponent, { injector: this.injector }));
        customElements.define('mc-layer-labels', createCustomElement(LayerLabelsComponent, { injector: this.injector }));
        customElements.define('mc-min-max-scale', createCustomElement(MinMaxScaleComponent, { injector: this.injector }));
        customElements.define('mc-legend', createCustomElement(LegendComponent, { injector: this.injector }));
        customElements.define('mc-legend-symbol', createCustomElement(LegendSymbolComponent, { injector: this.injector }));
        customElements.define('mc-legend-data', createCustomElement(LegendDataComponent, { injector: this.injector }));
        customElements.define('mc-pennbots-connector', createCustomElement(PennbotsConnectorComponent, { injector: this.injector }));
        customElements.define('mc-basemap-switcher', createCustomElement(BasemapSwitcherComponent, { injector: this.injector }));
        customElements.define('mc-expander', createCustomElement(ExpanderComponent, { injector: this.injector }));
        customElements.define('mc-map', createCustomElement(MapComponent, { injector: this.injector }));
        customElements.define('mc-import-external-map', createCustomElement(ImportExternalMapComponent, { injector: this.injector }));
        customElements.define('mc-import-shapefile', createCustomElement(ImportShapefileComponent, { injector: this.injector }));
        customElements.define('mc-map-info-container', createCustomElement(MapInfoContainerComponent, { injector: this.injector }));
        customElements.define('mc-map-picker', createCustomElement(MapPickerComponent, { injector: this.injector }));
        customElements.define('mc-layer-filter-container', createCustomElement(LayerFilterContainerComponent, { injector: this.injector }));
        customElements.define('mc-layer-filter-selector', createCustomElement(LayerFilterSelectorComponent, { injector: this.injector }));
        customElements.define('mc-isle', createCustomElement(IsleComponent, { injector: this.injector }));
        customElements.define('mc-irow', createCustomElement(IrowComponent, { injector: this.injector }));
        customElements.define('mc-search', createCustomElement(SearchComponent, { injector: this.injector }));
        customElements.define('mc-layer-spatial-query', createCustomElement(LayerSpatialQueryComponent, { injector: this.injector }));
        customElements.define('mc-layer-tooltip', createCustomElement(LayerTooltipComponent, { injector: this.injector }));
        customElements.define('mc-simple-toggle', createCustomElement(SimpleToggleComponent, { injector: this.injector }));
        customElements.define('mc-window', createCustomElement(WindowComponent, { injector: this.injector }));
        customElements.define('mc-tabs', createCustomElement(TabsComponent, { injector: this.injector }));
        customElements.define('mc-share-layer', createCustomElement(ShareLayerComponent, { injector: this.injector }));
        customElements.define('mc-map-catalog', createCustomElement(MapCatalogComponent, { injector: this.injector }));
        customElements.define('mc-map-catalog-item', createCustomElement(MapCatalogItemComponent, { injector: this.injector }));
        customElements.define('mc-unshare-layer', createCustomElement(UnshareLayerComponent, { injector: this.injector }));
        customElements.define('mc-auth', createCustomElement(AuthComponent, { injector: this.injector }));
        customElements.define('mc-asset-search', createCustomElement(AssetSearchComponent, { injector: this.injector }));
        customElements.define('mc-lat-long', createCustomElement(LatLongComponent, { injector: this.injector }));
        customElements.define('mc-address', createCustomElement(AddressComponent, { injector: this.injector }));
        customElements.define('mc-intersection', createCustomElement(IntersectionComponent, { injector: this.injector }));
        customElements.define('mc-interstate', createCustomElement(InterstateComponent, { injector: this.injector }));
        customElements.define('mc-region', createCustomElement(RegionComponent, { injector: this.injector }));
        customElements.define('mc-state', createCustomElement(StateComponent, { injector: this.injector }));
        customElements.define('mc-route', createCustomElement(RouteComponent, { injector: this.injector }));
        customElements.define('mc-routing', createCustomElement(RoutingComponent, { injector: this.injector }));
        customElements.define('mc-symbol-editor-container', createCustomElement(SymbolEditorContainerComponent, { injector: this.injector }));
        customElements.define('mc-point-symbol-editor', createCustomElement(PointSymbolEditorComponent, { injector: this.injector }));
        customElements.define('mc-line-symbol-editor', createCustomElement(LineSymbolEditorComponent, { injector: this.injector }));
        customElements.define('mc-polygon-symbol-editor', createCustomElement(PolygonSymbolEditorComponent, { injector: this.injector }));
        customElements.define('mc-text-symbol-editor', createCustomElement(TextSymbolEditorComponent, { injector: this.injector }));
        customElements.define('mc-heatmap-editor', createCustomElement(HeatmapEditorComponent, { injector: this.injector }));
        customElements.define('mc-layer-opacity-editor', createCustomElement(LayerOpacityEditorComponent, { injector: this.injector }));
        customElements.define('mc-share-map', createCustomElement(ShareMapComponent, { injector: this.injector }));
        customElements.define('mc-unshare-map', createCustomElement(UnShareMapComponent, { injector: this.injector }));
        customElements.define('mc-export-layer', createCustomElement(ExportLayerComponent, { injector: this.injector }));
        customElements.define('mc-export-map', createCustomElement(ExportMapComponent, { injector: this.injector }));
        customElements.define('mc-merge-map', createCustomElement(MergeMapComponent, { injector: this.injector }));
        customElements.define('mc-merge-map-action', createCustomElement(MergeMapActionComponent, { injector: this.injector }));
        customElements.define('mc-add-external-layer', createCustomElement(AddExternalLayerComponent, { injector: this.injector }));
        customElements.define('mc-external-layer-catalog', createCustomElement(ExternalLayerCatalogComponent, { injector: this.injector }));
        customElements.define('mc-save-map', createCustomElement(SaveMapComponent, { injector: this.injector }));
        customElements.define('mc-save-layer', createCustomElement(SaveLayerComponent, { injector: this.injector }));
        customElements.define('mc-theme-editor', createCustomElement(ThemeEditorComponent, { injector: this.injector }));
        customElements.define('mc-split-theme-editor', createCustomElement(SplitThemeEditorComponent, { injector: this.injector }));
        customElements.define('mc-user-settings', createCustomElement(UserSettingsComponent, { injector: this.injector }));
        customElements.define('mc-draw-text', createCustomElement(DrawTextComponent, { injector: this.injector }));
        customElements.define('mc-draw-point', createCustomElement(DrawPointComponent, { injector: this.injector }));
        customElements.define('mc-draw-line', createCustomElement(DrawLineComponent, { injector: this.injector }));
        customElements.define('mc-draw-polygon', createCustomElement(DrawPolygonComponent, { injector: this.injector }));
        customElements.define('mc-draw-intersection', createCustomElement(DrawIntersectionComponent, { injector: this.injector }));
        customElements.define('mc-area', createCustomElement(AreaComponent, { injector: this.injector }));
        customElements.define('mc-distance', createCustomElement(DistanceComponent, { injector: this.injector }));
        customElements.define('mc-identify', createCustomElement(IdentifyComponent, { injector: this.injector }));
        customElements.define('mc-application-messages', createCustomElement(ApplicationMessagesComponent, { injector: this.injector }));
        customElements.define('mc-print', createCustomElement(PrintComponent, { injector: this.injector }));
        customElements.define('mc-print-legend', createCustomElement(PrintLegendComponent, { injector: this.injector }));
        customElements.define('mc-buffer', createCustomElement(BufferComponent, { injector: this.injector }));
        customElements.define('mc-coordinates', createCustomElement(CoordinatesComponent, { injector: this.injector }));
        customElements.define('mc-street-view', createCustomElement(StreetViewComponent, { injector: this.injector }));
        customElements.define('mc-video-log-viewer', createCustomElement(VideoLogViewerComponent, { injector: this.injector }));
        customElements.define('mc-video-log-controller', createCustomElement(VideoLogControllerComponent, { injector: this.injector }));
        customElements.define('mc-video-log-info', createCustomElement(VideoLogInfoComponent, { injector: this.injector }));
        customElements.define('mc-lat-long-select', createCustomElement(LatLongSelectComponent, { injector: this.injector }));
        customElements.define('mc-rms-select', createCustomElement(RmsSelectComponent, { injector: this.injector }));
        customElements.define('mc-callout', createCustomElement(CalloutComponent, { injector: this.injector }));
        customElements.define('mc-overview-map', createCustomElement(OverviewMapComponent, { injector: this.injector }));
        customElements.define('mc-doc-source', createCustomElement(DocSourceComponent, { injector: this.injector }));
        customElements.define('mc-doc-source-item', createCustomElement(DocSourceItemComponent, { injector: this.injector }));
        customElements.define('mc-tree-explorer', createCustomElement(TreeExplorerComponent, { injector: this.injector }));
        customElements.define('mc-doc-source-photo', createCustomElement(DocSourcePhotoComponent, { injector: this.injector }));
        customElements.define('mc-doc-source-video', createCustomElement(DocSourceVideoComponent, { injector: this.injector }));
        customElements.define('mc-gallery', createCustomElement(GalleryComponent, { injector: this.injector }));
        customElements.define('mc-global-filter', createCustomElement(GlobalFilterComponent, { injector: this.injector }));
        customElements.define('mc-petl', createCustomElement(PetlComponent, { injector: this.injector }));
        customElements.define('mc-petl-about', createCustomElement(PetlAboutComponent, { injector: this.injector }));
        customElements.define('mc-petl-manage-projects', createCustomElement(PetlManageProjectsComponent, { injector: this.injector }));
        customElements.define('mc-petl-start', createCustomElement(PetlStartComponent, { injector: this.injector }));
        customElements.define('mc-petl-processor', createCustomElement(PetlProcessorComponent, { injector: this.injector }));
        customElements.define('mc-petl-column-mapping', createCustomElement(PetlColumnMappingComponent, { injector: this.injector }));
        customElements.define('mc-petl-validate', createCustomElement(PetlValidateComponent, { injector: this.injector }));
        customElements.define('mc-petl-promoted', createCustomElement(PetlPromotedComponent, { injector: this.injector }));
        customElements.define('mc-bookmark', createCustomElement(BookmarkComponent, { injector: this.injector }));
        customElements.define('mc-system-messages', createCustomElement(SystemMessagesComponent, { injector: this.injector }));
        customElements.define('mc-nearby', createCustomElement(NearbyComponent, { injector: this.injector }));
        customElements.define('mc-record-editor', createCustomElement(RecordEditorComponent, { injector: this.injector }));
        customElements.define('mc-scope', createCustomElement(ScopeComponent, { injector: this.injector }));
    }
}
